import React, { useState } from 'react';
import {
  Banner,
  Button,
  Card,
  CardFooter,
  CardHeader,
  CardSection,
  DateUtil,
  Flashy,
  Form,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  IconButton,
  Input,
  Layout,
  LayoutItem,
  Paragraph,
  ParagraphHeader,
  ReadonlyTextarea,
  SectionHeader,
  SmallHeader,
  Textarea,
  Well,
} from '@axiom/ui';
import type { UseOnboardingTextReturn } from '@axiom/ui';
import type {
  Opportunity,
  OpportunityOnboarding,
  OpportunityOnboardingPatchBody,
} from '@axiom/validation';

import type { OnboardingFirstDayForm } from '../../schema/onboarding-first-day-form-schema';
import { OnboardingFirstDayFormSchema } from '../../schema/onboarding-first-day-form-schema';
import { OpportunityApi } from '../../api/opportunity-api';

import type { OnboardingDetails } from './onboarding-util';

export type OnboardingFirstDayProps = {
  name: string;
  position: NonNullable<OpportunityOnboarding['positions']>[number];
  opportunityId: Opportunity['id'];
  firstDayText: Pick<
    NonNullable<ReturnType<UseOnboardingTextReturn['getOnboardingTexts']>>,
    'setUpSuccess' | 'startedWithAxiom'
  >;
};

const getKey = () => crypto.randomUUID();

export const OnboardingFirstDay = ({
  name,
  position,
  opportunityId,
  firstDayText,
}: OnboardingFirstDayProps) => {
  const isEditingLocked =
    DateUtil.getDaysSinceNow(position.startDate ?? new Date()) >= 10;

  const data: OnboardingDetails = position.candidateOpportunities?.[0]
    ?.onboardingDetails ?? {
    contact: null,
    notes: null,
    updatedAt: null,
    displayTimeUnit: null,
    displayTimeUnitUpdatedAt: null,
    additionalContacts: [],
  };

  const [initValues, setInitValues] = useState<OnboardingFirstDayForm>({
    notes: data.notes ?? '',
    clientName: data.contact?.name ?? '',
    jobTitle: data.contact?.jobTitle ?? null,
    email: data.contact?.email ?? null,
    phoneNumber: data.contact?.phoneNumber ?? null,
    additionalContacts: (data.additionalContacts ?? []).map(contact => {
      return {
        clientName: contact.name,
        jobTitle: contact.jobTitle ?? null,
        email: contact.email ?? null,
        phoneNumber: contact.phoneNumber ?? null,
        key: getKey(),
      };
    }),
  });

  return (
    <Form
      name="onboarding-first-day-form"
      schema={OnboardingFirstDayFormSchema}
      initialValues={initValues}
      onSubmit={async (_changedData, _actions, formData) => {
        const body: OpportunityOnboardingPatchBody = {
          notes: formData.notes,
          contact: {
            name: formData.clientName,
            jobTitle: formData.jobTitle ?? null,
            email: formData.email ?? null,
            phoneNumber: formData.phoneNumber ?? null,
          },
          additionalContacts: formData.additionalContacts.map(addContact => ({
            name: addContact.clientName,
            jobTitle: addContact.jobTitle ?? null,
            email: addContact.email ?? null,
            phoneNumber: addContact.phoneNumber ?? null,
          })),
        };

        if (position.candidateOpportunities?.[0]?.candidate.id) {
          await OpportunityApi.updateOpportunitiesOnboardingCandidate({
            opportunityId,
            candidateId: position.candidateOpportunities[0].candidate.id,
            body,
          });

          await OpportunityApi.refreshOpportunitiesOnboarding({
            opportunityId,
          });

          setInitValues({
            notes: formData.notes,
            clientName: formData.clientName,
            jobTitle: formData.jobTitle,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            additionalContacts: formData.additionalContacts,
          });
        }
      }}
    >
      {({ fireSubmit, values, setValues }) => {
        const addAdditionalContact = () => {
          void setValues({
            ...values,
            additionalContacts: [
              ...(values.additionalContacts ?? []),
              {
                clientName: '',
                jobTitle: null,
                email: null,
                phoneNumber: null,
                key: getKey(),
              },
            ],
          });
        };

        const removeAdditionalContact = (idx: number) => {
          void setValues({
            ...values,
            additionalContacts: (values.additionalContacts ?? []).toSpliced(
              idx,
              1
            ),
          });
        };

        return (
          <Card name={name}>
            <CardHeader>
              <SmallHeader>First Day Details</SmallHeader>
            </CardHeader>
            <CardSection divider>
              {isEditingLocked ? (
                <Gutter bottom="24px">
                  <Banner
                    impact="high"
                    type="warning"
                    name="EDITING_LOCKED_BANNER"
                  >
                    <Paragraph>
                      This section is no longer visible in AFT because the
                      engagement start date is older than 10 days, it can no
                      longer be updated
                    </Paragraph>
                  </Banner>
                </Gutter>
              ) : null}
              <Layout direction="horizontal">
                <LayoutItem rightGutter="8px">
                  <ParagraphHeader>Start date:</ParagraphHeader>
                </LayoutItem>
                <LayoutItem name="START_DATE">
                  {DateUtil.displayDateLongYear(position.startDate)}
                </LayoutItem>
              </Layout>
              <Gutter bottom="16px" />
              <Layout direction="horizontal">
                <LayoutItem rightGutter="8px">
                  <ParagraphHeader>Location of work:</ParagraphHeader>
                </LayoutItem>
                <LayoutItem name="LOCATION_OF_WORK">
                  {position.worksite}
                </LayoutItem>
              </Layout>
              <Gutter bottom="16px" />
              {isEditingLocked ? (
                <>
                  <ParagraphHeader>Custom note to talent:</ParagraphHeader>
                  <Gutter bottom="8px" />
                  <ReadonlyTextarea
                    name="notes"
                    value={data.notes ?? ''}
                    formatting="modern"
                  />
                </>
              ) : (
                <Textarea name="notes" label="Custom note to talent" />
              )}
              <Gutter bottom="24px" />
              <Well>
                <Grid stackable>
                  <GridRow columns={1} gutterBottom="16px">
                    <GridColumn>
                      <SectionHeader>Client contact information</SectionHeader>
                    </GridColumn>
                  </GridRow>
                  {isEditingLocked ? (
                    <>
                      <GridRow columns={2} gutterBottom="16px">
                        <GridColumn>
                          <ParagraphHeader>Clients Name</ParagraphHeader>
                          <Paragraph name="READONLY_CLIENT_NAME">
                            {initValues.clientName}
                          </Paragraph>
                        </GridColumn>
                        <GridColumn>
                          <ParagraphHeader>Job Title</ParagraphHeader>
                          <Paragraph name="READONLY_JOB_TITLE">
                            {initValues.jobTitle}
                          </Paragraph>
                        </GridColumn>
                      </GridRow>
                      <GridRow columns={2}>
                        <GridColumn>
                          <ParagraphHeader>Email</ParagraphHeader>
                          <Paragraph name="READONLY_EMAIL">
                            {initValues.email}
                          </Paragraph>
                        </GridColumn>
                        <GridColumn>
                          <ParagraphHeader>Phone Number</ParagraphHeader>
                          <Paragraph name="READONLY_PHONE_NUMBER">
                            {initValues.phoneNumber}
                          </Paragraph>
                        </GridColumn>
                      </GridRow>
                    </>
                  ) : (
                    <>
                      <GridRow columns={2} gutterBottom="16px">
                        <GridColumn>
                          <Input name="clientName" label="Clients Name" />
                          <Gutter bottom="16px" only="mobile" />
                        </GridColumn>
                        <GridColumn>
                          <Input name="jobTitle" label="Job Title" />
                        </GridColumn>
                      </GridRow>
                      <GridRow columns={2}>
                        <GridColumn>
                          <Input name="email" label="Email" />
                          <Gutter bottom="16px" only="mobile" />
                        </GridColumn>
                        <GridColumn>
                          <Input name="phoneNumber" label="Phone number" />
                        </GridColumn>
                      </GridRow>
                    </>
                  )}
                </Grid>
              </Well>
            </CardSection>
            {isEditingLocked ? (
              (data.additionalContacts ?? []).length > 0 ? (
                <CardSection divider>
                  <SectionHeader>Additional Client Contacts</SectionHeader>
                  <Gutter bottom="16px" />
                  <Layout
                    direction="vertical"
                    position="space-between"
                    verticalGutter="16px"
                  >
                    {(data.additionalContacts ?? []).map((contact, idx) => {
                      return (
                        <LayoutItem key={`${contact.name}-${contact.email}`}>
                          <Well name={`READONLY_CLIENT_CONTACT_${idx}`}>
                            <Grid stackable>
                              <GridRow columns={2} gutterBottom="16px">
                                <GridColumn>
                                  <ParagraphHeader>
                                    Clients Name
                                  </ParagraphHeader>
                                  <Paragraph
                                    name={`READONLY_CLIENT_CONTACT_${idx}_NAME`}
                                  >
                                    {contact.name}
                                  </Paragraph>
                                </GridColumn>
                                <GridColumn>
                                  <ParagraphHeader>Job Title</ParagraphHeader>
                                  <Paragraph
                                    name={`READONLY_CLIENT_CONTACT_${idx}_JOB_TITLE`}
                                  >
                                    {contact.jobTitle}
                                  </Paragraph>
                                </GridColumn>
                              </GridRow>
                              <GridRow columns={2}>
                                <GridColumn>
                                  <ParagraphHeader>Email</ParagraphHeader>
                                  <Paragraph
                                    name={`READONLY_CLIENT_CONTACT_${idx}_EMAIL`}
                                  >
                                    {contact.email}
                                  </Paragraph>
                                </GridColumn>
                                <GridColumn>
                                  <ParagraphHeader>
                                    Phone Number
                                  </ParagraphHeader>
                                  <Paragraph
                                    name={`READONLY_CLIENT_CONTACT_${idx}_PHONE_NUMBER`}
                                  >
                                    {contact.phoneNumber}
                                  </Paragraph>
                                </GridColumn>
                              </GridRow>
                            </Grid>
                          </Well>
                        </LayoutItem>
                      );
                    })}
                  </Layout>
                </CardSection>
              ) : null
            ) : (
              <CardSection divider>
                <SectionHeader>Additional Client Contacts</SectionHeader>
                <Gutter bottom="16px" />
                <Layout
                  direction="vertical"
                  position="space-between"
                  verticalGutter="16px"
                >
                  {(values.additionalContacts ?? []).map((contact, idx) => {
                    return (
                      <Layout direction="horizontal" key={contact.key}>
                        <LayoutItem fluid>
                          <Gutter right="16px">
                            <Well name={`CLIENT_CONTACT_${idx}`}>
                              <Grid stackable>
                                <GridRow columns={2} gutterBottom="16px">
                                  <GridColumn>
                                    <Input
                                      name={`additionalContacts.${idx}.clientName`}
                                      label="Clients Name"
                                    />
                                    <Gutter bottom="16px" only="mobile" />
                                  </GridColumn>
                                  <GridColumn>
                                    <Input
                                      name={`additionalContacts.${idx}.jobTitle`}
                                      label="Job Title"
                                    />
                                  </GridColumn>
                                </GridRow>
                                <GridRow columns={2}>
                                  <GridColumn>
                                    <Input
                                      name={`additionalContacts.${idx}.email`}
                                      label="Email"
                                    />
                                    <Gutter bottom="16px" only="mobile" />
                                  </GridColumn>
                                  <GridColumn>
                                    <Input
                                      name={`additionalContacts.${idx}.phoneNumber`}
                                      label="Phone number"
                                    />
                                  </GridColumn>
                                </GridRow>
                              </Grid>
                            </Well>
                          </Gutter>
                        </LayoutItem>
                        <LayoutItem>
                          <IconButton
                            icon="trash"
                            name="REMOVE_CONTACT"
                            onClick={() => {
                              removeAdditionalContact(idx);
                            }}
                            variation="minimal"
                            pattern="secondary"
                          />
                        </LayoutItem>
                      </Layout>
                    );
                  })}
                </Layout>
                {(values.additionalContacts ?? []).length > 0 ? (
                  <Gutter bottom="8px" />
                ) : null}
                <Button
                  icon="plus"
                  onClick={() => {
                    addAdditionalContact();
                  }}
                  name="ADD_CONTACT"
                  iconPosition="right"
                  variation="minimal"
                  pattern="secondary"
                >
                  New
                </Button>
              </CardSection>
            )}
            {firstDayText.setUpSuccess || firstDayText.startedWithAxiom ? (
              <CardSection divider>
                {firstDayText.setUpSuccess ? (
                  <>
                    <ParagraphHeader>Get Set Up for Success:</ParagraphHeader>
                    <Gutter bottom="8px" />
                    <ReadonlyTextarea
                      name="success"
                      value={firstDayText.setUpSuccess}
                      formatting="modern"
                    />
                  </>
                ) : null}
                {firstDayText.startedWithAxiom ? (
                  <>
                    <ParagraphHeader>
                      Getting Started With Axiom:
                    </ParagraphHeader>
                    <Gutter bottom="8px" />
                    <ReadonlyTextarea
                      name="startingWithAxiom"
                      value={firstDayText.startedWithAxiom}
                      formatting="modern"
                    />
                  </>
                ) : null}
              </CardSection>
            ) : null}
            {!isEditingLocked || data.updatedAt ? (
              <CardFooter>
                <Layout position="right middle">
                  {data.updatedAt ? (
                    <Flashy color="textSubtle" name="LAST_SAVED_TEXT">
                      Last saved on {DateUtil.displayTimestamp(data.updatedAt)}
                    </Flashy>
                  ) : null}
                  {isEditingLocked ? null : (
                    <>
                      {data.updatedAt ? <Gutter right="16px" /> : null}
                      <Button
                        name="SAVE_BUTTON"
                        onClick={() => {
                          void fireSubmit();
                        }}
                      >
                        Save
                      </Button>
                    </>
                  )}
                </Layout>
              </CardFooter>
            ) : null}
          </Card>
        );
      }}
    </Form>
  );
};
